import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input } from "reactstrap";
import { ChevronDown } from "react-feather";

import "./AssistiveSelect.scss";

export interface AssistiveSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  title?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;

  /** Fonte síncrona: array fixo de opções. Ignorado se `onSearchAsync` for passado. */
  options?: AssistiveSelectOption[];

  /**
   * Fonte assíncrona: chamado a cada mudança na busca (com debounce interno).
   * Retorna a lista de opções filtradas pelo termo.
   */
  onSearchAsync?: (query: string) => Promise<AssistiveSelectOption[]>;
  debounceMs?: number;

  /**
   * Opção fixa injetada no topo (ex: "All pages" no filtro de página).
   * Fica antes dos resultados do onSearchAsync ou do filtro local.
   */
  defaultOption?: AssistiveSelectOption;

  /** Input de busca dentro do menu. Default: true. */
  searchable?: boolean;

  /**
   * Estilo do botão que abre o menu.
   *  - `pill`: bordas arredondadas, visual de select (filtros da tabela)
   *  - `breadcrumb`: sem bordas, visual de link (breadcrumb do header)
   *  - `plain`: só o children, sem estilo — use quando o consumer renderiza o próprio toggle
   */
  toggleVariant?: "pill" | "chip" | "chip-lg" | "breadcrumb" | "plain";

  /** Conteúdo do toggle. Se omitido, usa `displayLabel` calculado a partir de value/options. */
  toggleContent?: React.ReactNode;

  placeholder?: string;
  emptyText?: string;
  loadingText?: string;
  searchPlaceholder?: string;

  minWidth?: number;
  maxHeight?: number;

  className?: string;
  disabled?: boolean;
  inNavbar?: boolean;

  /** Nome acessivel do combobox (aria-label). Fallback: displayLabel. */
  ariaLabel?: string;
}

const AssistiveSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  onSearchAsync,
  debounceMs = 250,
  defaultOption,
  searchable = true,
  toggleVariant = "pill",
  toggleContent,
  placeholder = "",
  emptyText,
  loadingText,
  searchPlaceholder,
  minWidth,
  maxHeight = 360,
  className,
  disabled,
  inNavbar,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [asyncOpts, setAsyncOpts] = useState<AssistiveSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  const autoId = useId();
  const menuId = `assistive-select-menu-${autoId}`;
  const optId = (v: string) => `${menuId}-opt-${v || "__default"}`;

  const isAsync = !!onSearchAsync;

  useEffect(() => {
    if (!open) return;
    setQ("");
    if (searchable) {
      const raf = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!isAsync) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await onSearchAsync!(q);
        setAsyncOpts(res || []);
      } catch {
        setAsyncOpts([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q, isAsync, debounceMs, onSearchAsync]);

  const filteredSync = useMemo(() => {
    if (isAsync || !options) return [];
    if (!q) return options;
    const needle = q.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, q, isAsync]);

  const currentList = isAsync ? asyncOpts : filteredSync;

  const displayLabel = useMemo(() => {
    if (toggleContent !== undefined) return null;
    const all: AssistiveSelectOption[] = [
      ...(defaultOption ? [defaultOption] : []),
      ...(options || []),
      ...(isAsync ? asyncOpts : []),
    ];
    const found = all.find((o) => o.value === value);
    return found?.label || placeholder;
  }, [toggleContent, value, defaultOption, options, asyncOpts, isAsync, placeholder]);

  const searchPh = searchPlaceholder || (t("general.typeToSearch") as string) || "Type to search…";
  const emptyTx = emptyText || (t("general.noResults") as string) || "No results";
  const loadingTx = loadingText || (t("general.loading") as string) || "Loading…";

  const toggleClass = "assistive-select__toggle assistive-select__toggle--" + toggleVariant;

  return (
    <Dropdown
      className={
        "assistive-select assistive-select--" + toggleVariant +
        (className ? " " + className : "")
      }
      isOpen={open}
      toggle={() => !disabled && setOpen((v) => !v)}
      inNavbar={inNavbar}
    >
      <DropdownToggle
        tag={toggleVariant === "breadcrumb" ? "div" : "button"}
        type={toggleVariant === "breadcrumb" ? undefined : "button"}
        className={toggleClass + (disabled ? " disabled" : "")}
        tabIndex={0}
        spellCheck={false}
        disabled={disabled}
        aria-label={ariaLabel || (typeof displayLabel === "string" ? displayLabel : undefined)}
        // ARIA passada via spread porque o type do reactstrap declara
        // aria-haspopup como boolean (errado — spec ARIA aceita "listbox").
        {...({
          "aria-haspopup": "listbox",
          "aria-expanded": open,
          "aria-controls": menuId,
        } as any)}
      >
        {toggleContent !== undefined ? (
          toggleContent
        ) : (
          <>
            <span className="assistive-select__toggle-text">{displayLabel}</span>
            <ChevronDown
              size={toggleVariant === "breadcrumb" ? 16 : toggleVariant === "chip" ? 12 : 14}
              className="assistive-select__toggle-caret"
              aria-hidden="true"
            />
          </>
        )}
      </DropdownToggle>
      <DropdownMenu
        id={menuId}
        role="listbox"
        aria-label={ariaLabel || (typeof displayLabel === "string" ? displayLabel : undefined)}
        className="assistive-select__menu"
        style={{ minWidth, maxHeight, overflowY: "auto" }}
      >
        {searchable && (
          <>
            <div className="assistive-select__search px-2 py-1">
              <Input
                type="text"
                className="input-default"
                placeholder={searchPh}
                aria-label={searchPh}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                innerRef={(el: HTMLInputElement) => { searchRef.current = el; }}
                spellCheck={false}
              />
            </div>
            <DropdownItem divider />
          </>
        )}
        {defaultOption && (
          <DropdownItem
            id={optId(defaultOption.value)}
            role="option"
            aria-selected={value === defaultOption.value}
            active={value === defaultOption.value}
            onClick={() => onChange(defaultOption.value)}
          >
            {defaultOption.label}
          </DropdownItem>
        )}
        {isAsync && loading && (
          <DropdownItem disabled aria-disabled="true">{loadingTx}</DropdownItem>
        )}
        {!loading && currentList.length === 0 && !defaultOption && (
          <DropdownItem disabled aria-disabled="true">{emptyTx}</DropdownItem>
        )}
        {!loading && currentList.map((o) => (
          <DropdownItem
            key={o.value}
            id={optId(o.value)}
            role="option"
            aria-selected={o.value === value}
            active={o.value === value}
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

export default AssistiveSelect;
